import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { MarketplacePropertyCategory_ReactionEventReaction } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { useRouter } from 'next/router';

import { useObservability } from '../../../helpers/observability';
import {
  BuyerFavoriteAddDocument,
  type BuyerFavoriteAddInput,
  BuyerFavoriteRemoveDocument,
  type BuyerFavoriteRemoveInput,
  type MarketplaceFavoriteOrigin,
  MarketplaceListingFavoriteStateDocument,
} from './__generated__/marketplace-favorites.zeus';

const redirectToLogin = (addressId: string) => {
  const url = new URL(window.location.toString());
  url.searchParams.set('likedId', addressId);
  window.location.assign(`/login?redirectTo=${encodeURIComponent(url.toString())}`);
};

type FavoriteState = {
  addressId?: string | null;
  isLiked: boolean;
  isKnown: boolean;
  isMutating: boolean;
};

type FavoriteAction =
  | {
      type: 'RESET';
      payload: { addressId?: string | null; isKnown: boolean; isLiked: boolean };
    }
  | {
      type: 'START_TOGGLE_FAVORITE';
      payload: { addressId: string };
    }
  | {
      type: 'SET_FAVORITE';
      payload: { addressId: string; isLiked: boolean };
    };

type FavoriteMutationResult = {
  isLiked: boolean;
  succeeded: boolean;
};

type FavoriteToggleAction = {
  analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction;
  mutate: () => Promise<FavoriteMutationResult>;
};

type TrackFavoriteSuccessOptions = {
  /** Address whose favorite state was persisted. */
  favoriteAddressId: string;
  /** Reaction persisted; ADD_LIKE additionally emits the add-only addFavorite event. */
  analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction;
};

const favoriteReducer = (state: FavoriteState, action: FavoriteAction): FavoriteState => {
  switch (action.type) {
    case 'RESET':
      return {
        addressId: action.payload.addressId,
        isKnown: action.payload.isKnown,
        isLiked: action.payload.isLiked,
        isMutating: false,
      };
    case 'START_TOGGLE_FAVORITE':
      if (state.addressId !== action.payload.addressId) {
        return state;
      }
      return { ...state, isKnown: true, isLiked: !state.isLiked, isMutating: true };
    case 'SET_FAVORITE':
      if (state.addressId !== action.payload.addressId) {
        return state;
      }
      return { ...state, isKnown: true, isLiked: action.payload.isLiked, isMutating: false };
  }
  action satisfies never;
  return state;
};

const favoriteAnalyticsSource = {
  GALLERY: 'REACTION_SOURCE_GALLERY',
  LISTING_DETAILS: 'REACTION_SOURCE_WEB_PDP',
} as const satisfies Record<MarketplaceFavoriteOrigin, string>;

const checkIfLiked = async (addressId: string) => {
  const result = await requestZeusFromBrowser(MarketplaceListingFavoriteStateDocument, {
    addressId,
  });
  return result.marketplace.listing?.isLiked ?? false;
};

const likeProperty = async (input: BuyerFavoriteAddInput): Promise<FavoriteMutationResult> => {
  try {
    const result = await requestZeusFromBrowser(BuyerFavoriteAddDocument, {
      input,
    });
    const succeeded =
      result.buyerFavoriteAdd.__typename === 'CommandResult' && result.buyerFavoriteAdd.success;
    return { isLiked: succeeded, succeeded };
  } catch {
    return { isLiked: false, succeeded: false };
  }
};

const unlikeProperty = async (input: BuyerFavoriteRemoveInput): Promise<FavoriteMutationResult> => {
  try {
    const result = await requestZeusFromBrowser(BuyerFavoriteRemoveDocument, {
      input,
    });
    const succeeded =
      result.buyerFavoriteRemove.__typename === 'CommandResult' &&
      result.buyerFavoriteRemove.success;
    return { isLiked: !succeeded, succeeded };
  } catch {
    return { isLiked: true, succeeded: false };
  }
};

type UseMarketplaceFavoriteOptions = {
  addressId?: string | null;
  origin?: MarketplaceFavoriteOrigin;
  initialIsLiked?: boolean | null;
  initialFavoritePending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  /** List price in cents for the addFavorite journey event; omit when the surface has no price loaded. */
  priceCents?: number | null;
};

export type MarketplaceFavoriteChangeHandler = (
  addressId: string,
  isLiked: boolean,
) => Promise<void>;

export const useMarketplaceFavorite = ({
  addressId,
  origin = 'LISTING_DETAILS',
  initialIsLiked,
  initialFavoritePending = false,
  onFavoriteChange,
  priceCents,
}: UseMarketplaceFavoriteOptions) => {
  const { authentication } = useAuth();
  const router = useRouter();
  const isLoggedIn = authentication.state === 'authenticated';
  const { trackJourneyEvent } = useObservability();
  const likedIdExists = router.query['likedId'] === addressId;
  const analyticsSource = favoriteAnalyticsSource[origin];
  const latestReadId = useRef(0);
  const pendingMutationAddressIds = useRef(new Set<string>());
  const [state, dispatch] = useReducer(favoriteReducer, {
    addressId,
    isKnown: initialIsLiked != null || !isLoggedIn,
    isLiked: initialIsLiked ?? false,
    isMutating: false,
  });
  const isFavoriteLoading =
    isLoggedIn && (state.addressId !== addressId || !state.isKnown || state.isMutating);

  const onFinishLikedId = useCallback(
    (likedAddressId: string) => {
      if (router.query['likedId'] !== likedAddressId) {
        return;
      }
      const { likedId: _, ...query } = router.query;
      void router.replace(
        {
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const trackFavoriteSuccess = useCallback(
    ({ favoriteAddressId, analyticsReaction }: TrackFavoriteSuccessOptions) => {
      trackJourneyEvent('marketplaceProperty', 'reaction', {
        addressUuid: favoriteAddressId,
        reaction: analyticsReaction,
        source: analyticsSource,
      });
      if (
        analyticsReaction === MarketplacePropertyCategory_ReactionEventReaction.REACTION_ADD_LIKE
      ) {
        trackJourneyEvent('marketplaceProperty', 'addFavorite', {
          addressUuid: favoriteAddressId,
          source: analyticsSource,
          ...(priceCents != null && { priceCents: BigInt(Math.round(priceCents)) }),
        });
      }
    },
    [analyticsSource, priceCents, trackJourneyEvent],
  );

  // The replay effect reads tracking through a ref: with trackFavoriteSuccess in
  // its dependencies, a mid-flight identity change (e.g. priceCents arriving once
  // the listing loads) would re-run the effect and persist the like twice.
  const trackFavoriteSuccessRef = useRef(trackFavoriteSuccess);
  useEffect(() => {
    trackFavoriteSuccessRef.current = trackFavoriteSuccess;
  }, [trackFavoriteSuccess]);

  useEffect(() => {
    latestReadId.current += 1;
    dispatch({
      type: 'RESET',
      payload: {
        addressId,
        isKnown: initialIsLiked != null || !isLoggedIn,
        isLiked: initialIsLiked ?? false,
      },
    });
  }, [addressId, initialIsLiked, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !addressId || !likedIdExists) {
      return undefined;
    }

    const favoriteAddressId = addressId;
    const readId = (latestReadId.current += 1);
    let cancelled = false;
    pendingMutationAddressIds.current.add(favoriteAddressId);
    dispatch({ type: 'START_TOGGLE_FAVORITE', payload: { addressId: favoriteAddressId } });

    async function replayFavoriteAfterLogin() {
      try {
        const result = await likeProperty({ addressId: favoriteAddressId, origin });
        if (!cancelled && readId === latestReadId.current) {
          dispatch({
            type: 'SET_FAVORITE',
            payload: { addressId: favoriteAddressId, isLiked: result.isLiked },
          });
          if (result.succeeded) {
            trackFavoriteSuccessRef.current({
              favoriteAddressId,
              analyticsReaction:
                MarketplacePropertyCategory_ReactionEventReaction.REACTION_ADD_LIKE,
            });
            await onFavoriteChange?.(favoriteAddressId, result.isLiked);
          }
        }
      } finally {
        pendingMutationAddressIds.current.delete(favoriteAddressId);
        onFinishLikedId(favoriteAddressId);
      }
    }

    void replayFavoriteAfterLogin();
    return () => {
      cancelled = true;
    };
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [addressId, isLoggedIn, likedIdExists, onFavoriteChange, onFinishLikedId, origin]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !addressId ||
      likedIdExists ||
      initialIsLiked != null ||
      initialFavoritePending
    ) {
      return undefined;
    }

    const favoriteAddressId = addressId;
    const readId = (latestReadId.current += 1);
    let cancelled = false;

    async function hydrateFavoriteState() {
      try {
        const isLiked = await checkIfLiked(favoriteAddressId);
        if (!cancelled && readId === latestReadId.current) {
          dispatch({ type: 'SET_FAVORITE', payload: { addressId: favoriteAddressId, isLiked } });
        }
      } catch {
        if (!cancelled && readId === latestReadId.current) {
          dispatch({
            type: 'SET_FAVORITE',
            payload: { addressId: favoriteAddressId, isLiked: false },
          });
        }
      }
    }

    void hydrateFavoriteState();
    return () => {
      cancelled = true;
    };
  }, [addressId, initialFavoritePending, initialIsLiked, isLoggedIn, likedIdExists]);

  const onToggleFavorite = useCallback(() => {
    if (!addressId) {
      return;
    }
    if (!isLoggedIn) {
      return redirectToLogin(addressId);
    }
    if (isFavoriteLoading) {
      return;
    }
    if (pendingMutationAddressIds.current.has(addressId)) {
      return;
    }

    const favoriteAddressId = addressId;
    pendingMutationAddressIds.current.add(favoriteAddressId);
    latestReadId.current += 1;
    const favoriteAction: FavoriteToggleAction = state.isLiked
      ? {
          analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction.REACTION_REMOVE_LIKE,
          mutate: () => unlikeProperty({ addressId: favoriteAddressId }),
        }
      : {
          analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction.REACTION_ADD_LIKE,
          mutate: () => likeProperty({ addressId: favoriteAddressId, origin }),
        };
    dispatch({
      type: 'START_TOGGLE_FAVORITE',
      payload: { addressId: favoriteAddressId },
    });

    async function persistFavoriteToggle() {
      try {
        const result = await favoriteAction.mutate();
        dispatch({
          type: 'SET_FAVORITE',
          payload: { addressId: favoriteAddressId, isLiked: result.isLiked },
        });
        if (!result.succeeded) {
          return;
        }
        trackFavoriteSuccess({
          favoriteAddressId,
          analyticsReaction: favoriteAction.analyticsReaction,
        });
        await onFavoriteChange?.(favoriteAddressId, result.isLiked);
      } finally {
        pendingMutationAddressIds.current.delete(favoriteAddressId);
      }
    }

    void persistFavoriteToggle();
  }, [
    addressId,
    isLoggedIn,
    isFavoriteLoading,
    // oxlint-disable-next-line react/memo-dependencies -- react19-baseline: pre-existing, do not copy. Fix the memo dependency array rather than suppressing
    onFavoriteChange,
    origin,
    state.isLiked,
    trackFavoriteSuccess,
  ]);

  return {
    onToggleFavorite,
    isFavoriteLoading,
    isLiked: state.addressId === addressId ? state.isLiked : (initialIsLiked ?? false),
  };
};
